<template>
  <b-modal
    ref="delegateModal"
    :title="modalTitle"
    @shown="createDelegateFormValidationContext"
    @hidden="destroyDelegateFormValidationContext"
  >
    <ValidationObserver ref="observer" slim>
      <b-form>
        <template v-if="contactsList && contactsList.length > 0">
          <b-form-group :label="$t('delegate.existing')">
            <multiselect
              v-model="selectedContact"
              :options="contactsList"
              track-by="email"
              :allow-empty="true"
              :placeholder="$t('delegate.select-contact')"
            >
              <template #option="{ option }">
                {{ option.first_name }} {{ option.last_name }}
                <br />
                <small class="text-muted">{{ option.email }}</small>
                <br v-if="option.account_name" />
                <small v-if="option.account_name" class="text-muted">
                  {{ option.account_name }}
                </small>
              </template>
              <template #singleLabel="{ option }">
                {{ option.first_name }} {{ option.last_name }}
                <br />
                <small class="text-muted">{{ option.email }}</small>
                <br v-if="option.account_name" />
                <small v-if="option.account_name" class="text-muted">
                  {{ option.account_name }}
                </small>
              </template>
            </multiselect>
          </b-form-group>
        </template>
        <template v-if="!selectedContact">
          <p v-if="contactsList && contactsList.length > 0" class="text-muted">
            - {{ $t('or') }} -
          </p>
          <b-form-row>
            <validating-group
              class="col-sm"
              :validation-context="validationContext"
              validation-path="first_name"
              :label="$t('first-name')"
              v-slot="{ state }"
            >
              <b-input v-model="form.first_name" :state="state" />
            </validating-group>
            <validating-group
              class="col-sm"
              :validation-context="validationContext"
              validation-path="last_name"
              :label="$t('last-name')"
              rules="required"
              v-slot="{ state }"
            >
              <b-input v-model="form.last_name" :state="state" />
            </validating-group>
          </b-form-row>
          <validating-group
            :validation-context="validationContext"
            validation-path="email"
            :label="$t('e-mail')"
            rules="required|email"
            v-slot="{ state }"
          >
            <b-input v-model="form.email" type="text" :state="state" />
          </validating-group>
        </template>
        <template v-if="delegateLink">
          <hr />
          <b-form-group :label="$t('delegate.invite-link')">
            <b-input-group>
              <b-form-input
                ref="linkInput"
                :value="delegateLink"
                readonly
                @focus="selectLinkInput"
                @click="selectLinkInput"
              />
              <b-input-group-append>
                <b-button variant="primary" @click="copyLink">
                  {{
                    linkCopied
                      ? $t('delegate.link-copied')
                      : $t('delegate.copy-link')
                  }}
                </b-button>
              </b-input-group-append>
            </b-input-group>
            <div class="mt-2 d-flex flex-wrap" style="gap: 0.5rem">
              <b-button
                variant="outline-success"
                size="sm"
                :href="shareUrls.whatsapp"
                target="_blank"
                rel="noopener"
              >
                {{ $t('delegate.share-via-whatsapp') }}
              </b-button>
              <b-button
                variant="outline-primary"
                size="sm"
                :href="shareUrls.sms"
              >
                {{ $t('delegate.share-via-sms') }}
              </b-button>
              <b-button
                variant="outline-primary"
                size="sm"
                :href="shareUrls.email"
              >
                {{ $t('delegate.share-via-external-email') }}
              </b-button>
            </div>
          </b-form-group>
        </template>
      </b-form>
    </ValidationObserver>
    <template #modal-footer="{ cancel }">
      <b-button
        variant="primary"
        :disabled="!hasValidContact || loadingLink"
        @click="sendEmail"
      >
        <b-spinner v-if="loadingLink" small class="mr-1" />
        {{ $t('delegate.send-email') }}
      </b-button>
      <b-button variant="outline-secondary" @click="cancel">
        {{ $t('close') }}
      </b-button>
    </template>
  </b-modal>
</template>

<script>
import axios from 'axios';

import {
  CONTEXT_CREATE,
  CONTEXT_DESTROY,
  STORE_API_ERRORS,
} from '@gid/vue-common/store/validation.module/types';
import { filterNonInputErrors } from '@gid/vue-common/store/validation.module/utils';

const emptyData = {
  first_name: null,
  last_name: null,
  email: null,
};
export default {
  data() {
    return {
      order_sfid: null,
      jobName: null,
      validationContext: 'delegate',
      contactsList: null,
      selectedContact: null,
      delegateLink: null,
      linkCopied: false,
      loadingLink: false,
      form: {
        ...emptyData,
      },
    };
  },
  computed: {
    hasValidContact() {
      if (this.selectedContact) return true;
      const email = this.form.email;
      return email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    },
    hasValidEmail() {
      const email = this.form.email;
      return email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    },
    modalTitle() {
      if (this.jobName) {
        return `Job ${this.jobName} – ${this.$t('delegate.title')}:`;
      }
      return this.$t('delegate.title') + ':';
    },
    shareUrls() {
      const link = this.delegateLink || '';
      const body = this.$t('delegate.share-body', { link });
      const subject = this.$t('delegate.share-subject');
      return {
        whatsapp: `https://wa.me/?text=${encodeURIComponent(body)}`,
        sms: `sms:?body=${encodeURIComponent(body)}`,
        email: `mailto:?subject=${encodeURIComponent(
          subject,
        )}&body=${encodeURIComponent(body)}`,
      };
    },
  },
  watch: {
    selectedContact(contact) {
      if (contact && contact.invite_link) {
        this.delegateLink = contact.invite_link;
        this.linkCopied = false;
      } else if (!contact) {
        this.delegateLink = null;
        this.linkCopied = false;
      }
    },
    'form.email'() {
      this.delegateLink = null;
      this.linkCopied = false;
      clearTimeout(this._emailDebounce);
      if (this.hasValidEmail) {
        this._emailDebounce = setTimeout(() => {
          this.fetchInviteLink();
        }, 1000);
      }
    },
  },
  beforeDestroy() {
    clearTimeout(this._emailDebounce);
    clearTimeout(this._copiedResetTimer);
  },
  methods: {
    show(order_sfid, jobName) {
      this.order_sfid = order_sfid;
      this.jobName = jobName || null;
      this.$refs.delegateModal.show();
    },
    fetchInviteLink() {
      this.loadingLink = true;
      axios
        .post(`/api/partner/orders/${this.order_sfid}/invite-link`, {
          email: this.form.email,
          first_name: this.form.first_name,
          last_name: this.form.last_name,
        })
        .then((response) => {
          this.delegateLink = response.data.link;
          this.linkCopied = false;
        })
        .catch(() => {})
        .finally(() => {
          this.loadingLink = false;
        });
    },
    sendEmail() {
      this.$refs.observer.validate().then((valid) => {
        if (valid) {
          const base = this.selectedContact || this.form;
          // qualifications is always implied "yes" now that the checkbox is
          // gone — the backend still requires the field when send_email=true.
          this.doDelegate(
            { ...base, qualifications: true, send_email: true },
            true,
          );
        }
      });
    },
    doDelegate(data, showSuccessToast) {
      this.loadingLink = true;
      axios
        .post(`/api/partner/orders/${this.order_sfid}/delegate`, data)
        .then((response) => {
          this.delegateLink = response.data.link;
          this.linkCopied = false;
          if (showSuccessToast) {
            this.$bvToast.toast(this.$t('delegate.success'), {
              title: this.$t('successful'),
              variant: 'success',
            });
          }
          this.$emit('success');
        })
        .catch((error) => {
          let showError = true;
          if (error?.response?.status == 422) {
            this.$store.dispatch(`validation/${STORE_API_ERRORS}`, {
              context: this.validationContext,
              errors: error.response.data.errors,
            });
            const nonInputErrors = filterNonInputErrors(
              error.response.data.errors,
              Object.keys(this.form),
            );
            showError = Object.keys(nonInputErrors).length > 0;
          }

          if (showError) {
            this.$bvToast.toast(this.$t('delegate.error'), {
              title: this.$t('_errors.general.title'),
              variant: 'danger',
            });
          }
        })
        .finally(() => {
          this.loadingLink = false;
        });
    },
    selectLinkInput() {
      const input = this.$refs.linkInput?.$el || this.$refs.linkInput;
      if (input && typeof input.select === 'function') {
        input.select();
      }
    },
    copyLink() {
      this.selectLinkInput();
      const link = this.delegateLink || '';
      const done = () => {
        this.linkCopied = true;
        clearTimeout(this._copiedResetTimer);
        this._copiedResetTimer = setTimeout(() => {
          this.linkCopied = false;
        }, 3000);
      };
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(link).then(done, () => {
          document.execCommand('copy');
          done();
        });
      } else {
        document.execCommand('copy');
        done();
      }
    },
    createDelegateFormValidationContext() {
      this.fetchContacts();
      this.$store.dispatch(`validation/${CONTEXT_CREATE}`, {
        context: this.validationContext,
      });
    },
    destroyDelegateFormValidationContext() {
      clearTimeout(this._emailDebounce);
      clearTimeout(this._copiedResetTimer);
      this.form = { ...emptyData };
      this.selectedContact = null;
      this.delegateLink = null;
      this.linkCopied = false;
      this.loadingLink = false;
      this.$store.dispatch(`validation/${CONTEXT_DESTROY}`, {
        context: this.validationContext,
      });
    },
    fetchContacts() {
      axios
        .get(`/api/partner/account/contacts/for-job/${this.order_sfid}`)
        .then((response) => {
          this.contactsList = response.data
            .filter(({ email }) => email)
            .map(
              ({
                first_name,
                last_name,
                email,
                invite_link,
                account_id,
                account_name,
              }) => ({
                first_name,
                last_name,
                email,
                invite_link,
                account_id,
                account_name,
              }),
            )
            .sort((a, b) => a.email.localeCompare(b.email));
          this.selectedContact = null;
        });
    },
  },
};
</script>
